import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users, Newspaper, BookOpen, Banknote, Inbox, Send, Calendar,
  Megaphone, ShoppingCart, TrendingUp, ArrowRight,
} from "lucide-react";
import api from "../../lib/api";

function KPICard({ icon: Icon, label, value, delta, testid }) {
  return (
    <div className="gai-card gai-sharp p-6 relative overflow-hidden" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div
          className="w-11 h-11 flex items-center justify-center"
          style={{ background: "var(--gai-red-soft)", color: "var(--gai-red)" }}
        >
          <Icon size={18} />
        </div>
        {delta && (
          <span className="text-[0.65rem] tracking-[0.18em] uppercase font-semibold" style={{ color: "var(--gai-grey-500)" }}>
            ↗ {delta}
          </span>
        )}
      </div>
      <div className="mt-6 serif text-4xl md:text-5xl leading-none" style={{ color: "var(--gai-black)", fontFamily: "'Playfair Display', serif" }}>{value}</div>
      <div className="mt-2 text-[0.7rem] font-bold tracking-[0.22em] uppercase" style={{ color: "var(--gai-grey-500)" }}>
        {label}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [k, setK] = useState(null);
  const [articles, setArticles] = useState([]);
  const [appts, setAppts] = useState([]);

  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setK(r.data)).catch(() => {});
    api.get("/articles?limit=4").then((r) => setArticles(r.data || [])).catch(() => {});
    api.get("/admin/appointments").then((r) => setAppts((r.data || []).slice(0, 4))).catch(() => {});
  }, []);

  const eur = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

  return (
    <div data-testid="admin-dashboard">
      <div className="flex items-end justify-between gap-6 flex-wrap mb-8">
        <div>
          <div className="text-[0.66rem] font-bold tracking-[0.24em] uppercase mb-2" style={{ color: "var(--gai-red)" }}>Overview</div>
          <h1 className="serif text-4xl md:text-5xl leading-none" style={{ color: "var(--gai-black)", fontFamily: "'Playfair Display', serif" }}>Tableau de bord</h1>
          <p className="mt-2 text-sm" style={{ color: "var(--gai-grey-600)" }}>
            Vue d&apos;ensemble des performances éditoriales et commerciales.
          </p>
        </div>
        <Link to="/admin/annonceurs" className="gai-btn-gold">
          <Megaphone size={14} /> Annonceurs
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        <KPICard icon={Users}      label="Clients actifs"    value={k?.clients_active ?? "—"} delta="+12%" testid="kpi-clients"   />
        <KPICard icon={Banknote}   label="Ventes du mois"    value={k ? eur(k.sales_month_eur) : "—"} delta={k?.sales_month_count ? `${k.sales_month_count} ventes` : null} testid="kpi-sales" />
        <KPICard icon={Inbox}      label="Leads reçus"       value={k?.leads_total ?? "—"} delta={k?.leads_new ? `${k.leads_new} nouveaux` : null} testid="kpi-leads" />
        <KPICard icon={Calendar}   label="RDV en attente"    value={k?.appointments_pending ?? "—"} testid="kpi-appointments" />
        <KPICard icon={Newspaper}  label="Articles publiés"  value={k?.articles_published ?? "—"} testid="kpi-articles" />
        <KPICard icon={BookOpen}   label="Magazines publiés" value={k?.magazines_published ?? "—"} testid="kpi-magazines" />
        <KPICard icon={Send}       label="Devis envoyés"     value={k?.quotes_sent ?? "—"} testid="kpi-quotes" />
        <KPICard icon={ShoppingCart} label="Achats confirmés" value={k?.purchases_paid ?? "—"} testid="kpi-purchases" />
      </div>

      <div className="mt-10 grid lg:grid-cols-2 gap-6">
        <section className="gai-card gai-sharp p-6" data-testid="dashboard-latest-articles">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2 text-[0.66rem] font-bold tracking-[0.22em] uppercase" style={{ color: "var(--gai-red)" }}>
              <TrendingUp size={12} /> Derniers articles
            </div>
            <Link to="/admin/articles" className="gai-btn-ghost text-xs">Voir tout <ArrowRight size={12} /></Link>
          </div>
          <ul className="space-y-3">
            {articles.length === 0 && <li className="text-sm" style={{ color: "var(--gai-grey-500)" }}>—</li>}
            {articles.map((a) => (
              <li key={a.id} className="pb-3 last:border-b-0" style={{ borderBottom: "1px solid var(--gai-grey-200)" }}>
                <div className="flex items-start gap-2 w-full">
                  <span className="w-1.5 h-1.5 rounded-full mt-2 shrink-0" style={{ background: "var(--gai-red)" }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate" style={{ color: "var(--gai-black)" }}>{a.title?.fr || a.title?.en}</div>
                    <div className="text-[0.7rem] tracking-[0.18em] uppercase mt-1" style={{ color: "var(--gai-grey-500)" }}>
                      {a.category} · {a.author}
                    </div>
                  </div>
                  {a.is_premium && (
                    <span className="text-[0.6rem] tracking-[0.18em] uppercase px-2 py-0.5 font-bold shrink-0" style={{ background: "var(--gai-red)", color: "var(--gai-white)" }}>
                      Premium
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>

        <section className="gai-card gai-sharp p-6" data-testid="dashboard-next-appts">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2 text-[0.66rem] font-bold tracking-[0.22em] uppercase" style={{ color: "var(--gai-red)" }}>
              <Calendar size={12} /> Prochains rendez-vous
            </div>
            <Link to="/admin/rdv" className="gai-btn-ghost text-xs">Voir tout <ArrowRight size={12} /></Link>
          </div>
          <ul className="space-y-3">
            {appts.length === 0 && <li className="text-sm" style={{ color: "var(--gai-grey-500)" }}>Aucun rendez-vous programmé.</li>}
            {appts.map((r) => (
              <li key={r.id} className="pb-3 last:border-b-0 flex items-start gap-3" style={{ borderBottom: "1px solid var(--gai-grey-200)" }}>
                <div className="w-11 h-11 flex flex-col items-center justify-center" style={{ background: "var(--gai-red-soft)" }}>
                  <div className="serif text-lg leading-none" style={{ color: "var(--gai-red)", fontFamily: "'Playfair Display', serif" }}>
                    {(() => {
                      const d = r.preferred_date || r.date || r.scheduled_at;
                      if (!d) return "—";
                      const parsed = new Date(d);
                      return isNaN(parsed) ? "—" : String(parsed.getDate()).padStart(2, "0");
                    })()}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate" style={{ color: "var(--gai-black)" }}>{r.topic}</div>
                  <div className="text-xs mt-1 truncate" style={{ color: "var(--gai-grey-600)" }}>
                    {r.full_name} {r.company ? `· ${r.company}` : ""}
                  </div>
                </div>
                <span className="text-[0.66rem] font-bold tracking-[0.22em] uppercase shrink-0" style={{ color: "var(--gai-red)" }}>{r.status}</span>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}
